import React, { useEffect, useMemo, useState } from 'react';
import { ApolloError, useApolloClient } from '@apollo/client';
import Alert from '@amzn/awsui-components-console/alert';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import Table from '@amzn/awsui-components-console/table';
import { getAppSyncErrorType } from 'src/api/graphqlErrorType';
import { formatDayMonthYear } from 'src/common/utils/dateFormatters';
import {
  DescribeStringDocument,
  DescribeStringQuery,
  DescribeStringQueryVariables,
  useListStringGroupVersionsQuery,
} from 'src/generated/graphql';
import { NOT_AVAILABLE } from '../constants';
import {
  diffTextField,
  earlierVersionsToCompare,
  FieldDiff,
} from '../utils/versionFieldDiff';
import { mapWithConcurrency } from '../utils/mapWithConcurrency';
import StringVersionDiffModal from './StringVersionDiffModal';
import StringVersionViewModal from './StringVersionViewModal';

/**
 * Most earlier versions compared in one panel, matching the string-group page's
 * intentional cap (`useVersionHistory` requests `maxResults: 25`).
 *
 * There is no "Load more": each extra version costs one describeString call, and
 * `listStringGroupVersions` paginates with an opaque nextToken, so continuing
 * would mean holding cursor state to buy an unbounded per-version fan-out. The
 * latest 25 is what the group page shows too, so the two views agree.
 */
const MAX_VERSIONS_TO_COMPARE = 25;

/** Snapshot requests in flight at once — see mapWithConcurrency. */
const SNAPSHOT_BATCH_SIZE = 5;

/**
 * Width of the Actions column, wide enough to keep Diff and View on ONE line.
 *
 * The buttons sit in a horizontal `SpaceBetween`, which is a `flex-wrap: wrap`
 * row — too narrow a column stacks them instead of overflowing. Measured in the
 * browser: the wrap threshold is 196px (two buttons need 156px, and Cloudscape
 * spends 40px of the column on cell chrome), so this sits ~24px clear of it.
 *
 * `minWidth` as well as `width` because this table is not `resizableColumns`, so
 * `table-layout` stays `auto`, where a width is only a suggestion the browser may
 * compress — `minWidth` is the hard floor. Surplus goes to `Change`, which has no
 * width of its own. Matches `SearchResultsTable`, which pairs both on its actions
 * column.
 */
const ACTIONS_COLUMN_WIDTH = 220;

/**
 * "DD Mon YYYY" via the shared formatter, so this date reads the same as every
 * other date in the app, and matches the three sibling panels `84ab829` migrated
 * — string-group `MetadataPanel`/`ViewVersionModal` and `namespace/utils`.
 *
 * The em dash, not the date module's `NOT_AVAILABLE` ('NA'): that module reserves
 * 'NA' for a definite absence (a cancelled job has no delivery date) and keeps
 * the em dash for empty/unknown, which is what a missing `updatedOn` is here.
 *
 * The falsy guard carries the null handling: `updatedOn` is a nullable `Float`, so
 * an absent date and an explicit 0 both render the placeholder rather than
 * 01 Jan 1970.
 */
function formatDate(timestamp: number): string {
  if (!timestamp) {
    return NOT_AVAILABLE;
  }
  return formatDayMonthYear(new Date(timestamp));
}

interface VersionHistoryPanelProps {
  namespaceId: string;
  stringGroupId: string;
  stringId: string;
  /** Variant CX ID, so historical calls resolve the same variant. */
  cxId?: string;
  /** Current group version, as returned by describeString (stringified). */
  currentVersion: string;
  /**
   * Whether the page is showing a pinned historical version rather than the
   * latest one. History is then not offered at all — see the component doc.
   * Required (not defaulted) so a caller cannot silently get the broken view by
   * omission.
   */
  isPinnedVersion: boolean;
  onClose: () => void;
}

/**
 * One version's describeString outcome, before it is paired with a predecessor.
 *
 * `absent` is a `ResourceNotFoundException`: the string did not exist in that
 * group version. `noOverride` is the variant-only case where the requested CX
 * override did not exist yet — see the `resolvedCxId` check in the snapshot
 * effect.
 */
type Snapshot =
  | {
      version: number;
      status: 'loaded';
      sourceText: string;
      contextNote: string;
    }
  | { version: number; status: 'absent' }
  | { version: number; status: 'noOverride' }
  | { version: number; status: 'failed'; errorMessage: string };

/**
 * One table row. Modelled as a union so the renderer cannot reach a "resolved
 * row with no diff" state that would never occur.
 *
 * `created` is only used where creation is provable; `oldestLoaded` is the
 * can't-tell case and must not claim it.
 */
type HistoricalVersion =
  | {
      version: number;
      status: 'diffed';
      previousVersion: number;
      sourceText: FieldDiff;
      contextNote: FieldDiff;
    }
  | {
      version: number;
      status: 'created' | 'oldestLoaded';
      sourceText: string;
      contextNote: string;
    }
  | { version: number; status: 'noOverride' }
  | { version: number; status: 'failed'; errorMessage: string };

/** The four row summaries. `changed`/`unchanged` are both diffed rows. */
type SummaryState = 'changed' | 'unchanged' | 'created' | 'oldestLoaded';

const SUMMARY_LABEL: Record<SummaryState, string> = {
  changed: 'Changed',
  unchanged: 'No change',
  created: 'String created',
  oldestLoaded: 'Content at this version',
};

const SUMMARY_COLOR: Record<SummaryState, 'red' | 'grey' | 'blue'> = {
  changed: 'red',
  unchanged: 'grey',
  created: 'blue',
  oldestLoaded: 'grey',
};

/** Which modal is open, carrying the data it renders. */
type ActiveModal =
  | {
      kind: 'view';
      version: number;
      sourceText: string;
      contextNote: string;
    }
  | {
      kind: 'diff';
      version: number;
      previousVersion: number;
      sourceText: FieldDiff;
      contextNote: FieldDiff;
    };

/**
 * Pairs each snapshot with the next one DOWN the list — its predecessor — so a
 * row says what that version changed, not how it differs from today.
 *
 * Versions the string predates are dropped rather than rendered as failures.
 * `predecessorOutsideWindow` is true when an older version exists that was not
 * fetched, which is what stops the oldest row from claiming creation.
 */
function buildRows(
  snapshots: readonly Snapshot[],
  predecessorOutsideWindow: boolean,
): HistoricalVersion[] {
  const rows: HistoricalVersion[] = [];
  snapshots.forEach((snapshot, index) => {
    if (snapshot.status === 'absent') {
      return;
    }
    if (snapshot.status === 'failed' || snapshot.status === 'noOverride') {
      rows.push(snapshot);
      return;
    }
    const predecessor = snapshots[index + 1];
    if (predecessor?.status === 'loaded') {
      rows.push({
        version: snapshot.version,
        status: 'diffed',
        previousVersion: predecessor.version,
        sourceText: diffTextField(predecessor.sourceText, snapshot.sourceText),
        contextNote: diffTextField(
          predecessor.contextNote,
          snapshot.contextNote,
        ),
      });
      return;
    }
    // No comparable predecessor. A 404 predecessor proves the string starts
    // here, and so does the end of an exhaustive list. Anything else — a failed
    // predecessor, or an older version we never fetched — is not proof.
    const created =
      predecessor?.status === 'absent' ||
      (predecessor === undefined && !predecessorOutsideWindow);
    rows.push({
      version: snapshot.version,
      status: created ? 'created' : 'oldestLoaded',
      sourceText: snapshot.sourceText,
      contextNote: snapshot.contextNote,
    });
  });
  return rows;
}

/** True for AppSync's "string absent from that group version" failure. */
function isStringNotFound(error: unknown): boolean {
  const graphQLErrors = (error as ApolloError | undefined)?.graphQLErrors;
  return (
    graphQLErrors?.some(
      (e) => getAppSyncErrorType(e) === 'ResourceNotFoundException',
    ) ?? false
  );
}

/**
 * The row's summary, or null for a row with no content of its own — a failed
 * snapshot, or a variant version that had no override. Those rows keep their
 * own message and get no actions.
 */
function summaryOf(row: HistoricalVersion): SummaryState | null {
  if (row.status === 'failed' || row.status === 'noOverride') {
    return null;
  }
  if (row.status !== 'diffed') {
    return row.status;
  }
  return row.sourceText.changed || row.contextNote.changed
    ? 'changed'
    : 'unchanged';
}

/**
 * This version's own values plus, when it actually differs from its
 * predecessor, the pair the Diff modal needs. Null when the row has no content
 * of its own — it then gets no actions.
 *
 * A diffed row's `value` side IS this version's value — see `diffTextField` —
 * so every action is served from data the panel already holds.
 */
function rowActions(row: HistoricalVersion): {
  view: { sourceText: string; contextNote: string };
  diff?: {
    previousVersion: number;
    sourceText: FieldDiff;
    contextNote: FieldDiff;
  };
} | null {
  if (row.status === 'diffed') {
    return {
      view: {
        sourceText: row.sourceText.value,
        contextNote: row.contextNote.value,
      },
      // No Diff for a version that changed nothing: both columns would hold
      // the same value.
      ...(summaryOf(row) === 'changed'
        ? {
            diff: {
              previousVersion: row.previousVersion,
              sourceText: row.sourceText,
              contextNote: row.contextNote,
            },
          }
        : {}),
    };
  }
  if (row.status === 'created' || row.status === 'oldestLoaded') {
    return {
      view: { sourceText: row.sourceText, contextNote: row.contextNote },
    };
  }
  return null;
}

/**
 * Version history for a single string, rendered only for LangHub-owned sources.
 *
 * There is no per-string history API: `listStringGroupVersions` covers string
 * GROUPS. So history is assembled in two steps — a single bounded page of the
 * group's ACTUAL versions, then one `describeString` per earlier version.
 *
 * Each row reports what THAT version changed: its source text and context note
 * are diffed against the next version DOWN the same list, not against the
 * version on screen. Every snapshot a row needs is therefore already fetched —
 * the predecessor of one row is the next row.
 *
 * A row summarises its change with a badge and offers View (this version's
 * snapshot) plus, when it actually differs from its predecessor, Diff. Both
 * modals render from the snapshots above, so neither costs a request.
 *
 * Versions the string predates (`ResourceNotFoundException`) are dropped rather
 * than shown as errors, and the oldest row only claims creation when that is
 * provable — see `buildRows`.
 *
 * Both bounds matter. Taking the versions from the API rather than assuming a
 * contiguous 1…N-1 range keeps the requests to versions that exist, and the
 * snapshot calls run in small batches so a long-lived group cannot open hundreds
 * of concurrent requests when the panel is opened.
 *
 * **Offered on the latest version only.** The comparison needs the window of
 * versions immediately BELOW the one on screen, and `listStringGroupVersions`
 * cannot express that: its only arguments are `sortOrder`, `maxResults` and an
 * opaque `nextToken` — there is no upper-version bound or filter. On the latest
 * view a single `sortOrder: DESC` page is exactly the right window, but for a
 * pinned old version it is the wrong one, and walking `nextToken` to reach it is
 * unbounded in the case that matters (v2 of a 500-version group is ~20 pages in,
 * so any sane page cap still misses it) — which is how the panel came to render
 * an empty table and claim the pinned version was the group's earliest. Rather
 * than guess, a pinned view says where history lives; `isPinnedVersion` also
 * skips the version-list query, so nothing is fetched.
 *
 * The parent mounts this component only while the panel is open, which is what
 * makes the fetch lazy — nothing is requested until the user opens it.
 */
const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  namespaceId,
  stringGroupId,
  stringId,
  cxId,
  currentVersion,
  isPinnedVersion,
  onClose,
}) => {
  const client = useApolloClient();
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);
  // Both modals are fed from the row that opened them, so opening one issues no
  // request.
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);

  // The generated hook is used directly rather than the group page's
  // `useVersionHistory`: that hook re-maps its result on every render, so the
  // derived version list would get a new identity each pass and re-trigger the
  // snapshot effect below. Apollo's `data` is referentially stable, so both the
  // version list the effect depends on and the date lookup the table reads are
  // derived from it independently — see `updatedOnByVersion`.
  const {
    data: versionsData,
    loading: versionsLoading,
    error: versionsError,
  } = useListStringGroupVersionsQuery({
    variables: {
      namespaceId,
      stringGroupId,
      sortOrder: 'DESC',
      maxResults: MAX_VERSIONS_TO_COMPARE,
    },
    skip: isPinnedVersion,
  });

  const versions = useMemo(
    () =>
      earlierVersionsToCompare(
        (versionsData?.listStringGroupVersions.versions ?? []).map(
          (v) => v.version,
        ),
        currentVersion,
        MAX_VERSIONS_TO_COMPARE,
      ),
    [versionsData, currentVersion],
  );

  /**
   * Dates for the table, kept OUT of `versions` on purpose.
   *
   * `versions` is the snapshot effect's dependency, and that effect fires one
   * `describeString` per version. Widening it to carry dates would make its
   * identity depend on freshly built objects, so any re-render that rebuilt them
   * would re-run the effect and fan out the whole batch of requests again. The
   * dates therefore live in a separate lookup that nothing depends on: it is
   * memoised on the same referentially-stable `versionsData`, and only the `cell`
   * renderer reads it.
   */
  const updatedOnByVersion = useMemo(
    () =>
      new Map(
        (versionsData?.listStringGroupVersions.versions ?? []).map((v) => [
          v.version,
          v.updatedOn,
        ]),
      ),
    [versionsData],
  );

  /**
   * Whether a version older than the oldest one fetched may exist. A `nextToken`
   * says so outright; an exactly-full page WITHOUT one means the current version
   * was missing from it, so the list and `describeString` disagree (stale list,
   * or a version written since) and it cannot be trusted as exhaustive.
   */
  const predecessorOutsideWindow =
    Boolean(versionsData?.listStringGroupVersions.nextToken) ||
    versions.length >= MAX_VERSIONS_TO_COMPARE;

  useEffect(() => {
    if (versions.length === 0) {
      setSnapshots([]);
      // A previous run may still be in flight: its cleanup cancels the pending
      // `.then`, which returns without clearing the flag. Reset it here or the
      // spinner would stay up for a panel that now has nothing to load.
      setLoadingSnapshots(false);
      return;
    }

    // Guards against a state write after the panel closes (or its inputs
    // change) while requests are still in flight.
    let cancelled = false;
    setLoadingSnapshots(true);

    // One request per listed version, at most SNAPSHOT_BATCH_SIZE at a time.
    // Each is settled independently so a single failing version degrades to an
    // error row instead of blanking the whole panel.
    mapWithConcurrency(
      versions,
      SNAPSHOT_BATCH_SIZE,
      async (version): Promise<Snapshot> => {
        try {
          const response = await client.query<
            DescribeStringQuery,
            DescribeStringQueryVariables
          >({
            query: DescribeStringDocument,
            variables: {
              namespaceId,
              stringGroupId,
              stringId,
              cxId: cxId || undefined,
              stringGroupVersion: String(version),
            },
          });
          const historical = response.data.describeString;
          // In variant view the backend FALLS BACK to the base string when the
          // requested override does not exist in that version, signalling it by
          // returning `resolvedCxId: null` (or another CX ID) instead of failing.
          // Presenting that base text as the variant's own value would fabricate
          // a change, so the mismatch is reported as its own row state.
          if (cxId && historical.resolvedCxId !== cxId) {
            return { version, status: 'noOverride' };
          }
          return {
            version,
            status: 'loaded',
            sourceText: historical.sourceText,
            contextNote: historical.contextNote ?? '',
          };
        } catch (err) {
          // The string simply did not exist yet in that version — not a failure
          // to report. Its row is dropped, and it also proves the version above
          // it is where the string was created.
          if (isStringNotFound(err)) {
            return { version, status: 'absent' };
          }
          return {
            version,
            status: 'failed',
            errorMessage:
              err instanceof Error ? err.message : 'Unknown error occurred.',
          };
        }
      },
    ).then((results) => {
      if (cancelled) {
        return;
      }
      setSnapshots(results);
      setLoadingSnapshots(false);
    });

    return () => {
      cancelled = true;
    };
  }, [client, namespaceId, stringGroupId, stringId, cxId, versions]);

  const entries = useMemo(
    () => buildRows(snapshots, predecessorOutsideWindow),
    [snapshots, predecessorOutsideWindow],
  );

  const hasHistory = versions.length > 0;
  // `loadingSnapshots` alone leaves a one-render gap: it starts false and the
  // snapshot effect only runs after the commit, so the first render in which the
  // version list has resolved with earlier versions still has no snapshots and
  // the flag down — long enough for the table to paint zero rows. Treating
  // "history exists but nothing fetched yet" as loading closes that window. It
  // always clears: every version yields a snapshot, an error included, so a
  // finished run leaves `snapshots.length === versions.length`.
  const loading =
    versionsLoading ||
    loadingSnapshots ||
    (hasHistory && snapshots.length === 0);
  // Rows can be fewer than snapshots: versions the string predates are dropped.
  const hasRows = entries.length > 0;

  return (
    <Container
      data-testid="version-history-panel"
      header={
        <Header
          variant="h2"
          actions={
            <Button
              variant="icon"
              iconName="close"
              ariaLabel="Close version history"
              data-testid="version-history-close"
              onClick={onClose}
            />
          }
        >
          Version History
        </Header>
      }
    >
      <SpaceBetween size="s">
        {isPinnedVersion && (
          <Box
            variant="small"
            color="text-status-inactive"
            data-testid="version-history-pinned-note"
          >
            Version history is available on the current version. Clear the
            pinned version to compare this string across earlier versions.
          </Box>
        )}
        {!isPinnedVersion && versionsError && (
          <Alert
            type="error"
            header="Version history unavailable"
            data-testid="version-history-error"
          >
            {`The group's version list could not be loaded: ${versionsError.message}`}
          </Alert>
        )}
        {!isPinnedVersion && !versionsError && (
          <Box
            variant="small"
            color="text-status-inactive"
            data-testid="version-history-note"
          >
            {!hasHistory &&
              `No earlier versions to compare — v${currentVersion} is the earliest version of this group.`}
            {hasHistory &&
              (loading || hasRows) &&
              `Each version is compared with the version before it — at most ${MAX_VERSIONS_TO_COMPARE} versions are shown.`}
            {hasHistory &&
              !loading &&
              !hasRows &&
              !predecessorOutsideWindow &&
              `This string does not appear in any earlier version — it was created in v${currentVersion}.`}
            {/* Older versions were never fetched, so global absence — and the
                creation claim that follows from it — is not knowable here. Same
                reason `buildRows` refuses a per-row creation claim. */}
            {hasHistory &&
              !loading &&
              !hasRows &&
              predecessorOutsideWindow &&
              `This string does not appear in any of the latest ${MAX_VERSIONS_TO_COMPARE} versions of this group — older versions were not checked.`}
          </Box>
        )}
        {loading && (
          <Box textAlign="center" padding="m">
            <Spinner size="normal" /> Loading version history…
          </Box>
        )}
        {!loading && hasRows && (
          <Table
            items={entries}
            columnDefinitions={[
              {
                id: 'version',
                header: 'Version',
                cell: (item) => (
                  <span data-testid="version-item" data-version={item.version}>
                    <Box fontWeight="bold">{`v${item.version}`}</Box>
                  </span>
                ),
                width: 100,
              },
              {
                id: 'date',
                header: 'Date',
                cell: (item) => (
                  <span data-testid="version-date">
                    <Box variant="p">
                      {formatDate(updatedOnByVersion.get(item.version) ?? 0)}
                    </Box>
                  </span>
                ),
                width: 130,
              },
              {
                id: 'summary',
                header: 'Change',
                cell: (item) => {
                  const summary = summaryOf(item);
                  if (summary) {
                    return (
                      <span data-testid="version-summary" data-state={summary}>
                        <Badge color={SUMMARY_COLOR[summary]}>
                          {SUMMARY_LABEL[summary]}
                        </Badge>
                      </span>
                    );
                  }
                  if (item.status === 'failed') {
                    return (
                      <Alert type="error" data-testid="version-error">
                        {`Could not load v${item.version}: ${item.errorMessage}`}
                      </Alert>
                    );
                  }
                  return (
                    <span data-testid="version-no-override">
                      <SpaceBetween size="xxs">
                        <Badge color="grey">New</Badge>
                        <Box variant="p" color="text-status-inactive">
                          {`Override did not exist in this version — ${cxId} used the base string.`}
                        </Box>
                      </SpaceBetween>
                    </span>
                  );
                },
              },
              {
                id: 'actions',
                header: 'Actions',
                cell: (item) => {
                  const actions = rowActions(item);
                  // Failed and no-override rows carry no snapshot, so neither
                  // modal would have anything to show.
                  if (!actions) {
                    return (
                      <Box
                        color="text-status-inactive"
                        data-testid="version-actions-none"
                      >
                        {NOT_AVAILABLE}
                      </Box>
                    );
                  }
                  const { view, diff } = actions;
                  return (
                    <SpaceBetween direction="horizontal" size="xs">
                      {diff && (
                        <Button
                          variant="normal"
                          data-testid="version-action-diff"
                          ariaLabel={`Show diff for version ${item.version}`}
                          onClick={() =>
                            setActiveModal({
                              kind: 'diff',
                              version: item.version,
                              ...diff,
                            })
                          }
                        >
                          Diff
                        </Button>
                      )}
                      <Button
                        variant="normal"
                        data-testid="version-action-view"
                        ariaLabel={`View version ${item.version} snapshot`}
                        onClick={() =>
                          setActiveModal({
                            kind: 'view',
                            version: item.version,
                            ...view,
                          })
                        }
                      >
                        View
                      </Button>
                    </SpaceBetween>
                  );
                },
                width: ACTIONS_COLUMN_WIDTH,
                minWidth: ACTIONS_COLUMN_WIDTH,
              },
            ]}
          />
        )}
      </SpaceBetween>
      {activeModal?.kind === 'view' && (
        <StringVersionViewModal
          visible
          stringId={stringId}
          version={activeModal.version}
          sourceText={activeModal.sourceText}
          contextNote={activeModal.contextNote}
          onDismiss={() => setActiveModal(null)}
        />
      )}
      {activeModal?.kind === 'diff' && (
        <StringVersionDiffModal
          visible
          stringId={stringId}
          version={activeModal.version}
          previousVersion={activeModal.previousVersion}
          sourceText={activeModal.sourceText}
          contextNote={activeModal.contextNote}
          onDismiss={() => setActiveModal(null)}
        />
      )}
    </Container>
  );
};

export default VersionHistoryPanel;
